import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Provider, useSelector } from 'react-redux'
import { NotificationContainer } from 'react-notifications'
import { setupListeners } from '@reduxjs/toolkit/query/react'
import { Button, EmptyState, ErrorState, LoadingState } from '../../group_leader/components/common'
import { makeT } from '../../group_leader/helpers/i18n'
import { setMiscData } from '../../group_leader/store/miscSlice'
import {
  useListRoundsQuery,
  useGetTeeSheetQuery,
  useGetRoundInformationQuery,
  useGetTournamentResultsWidgetQuery,
  useToggleTournamentStatusMutation,
  useSendScoringLinksMutation,
  useListUserScoredResultsQuery,
  useAdjustUserScoredResultMutation,
  useClearUserScoredResultMutation,
  useRerankUserScoredEventMutation,
} from '../../group_leader/store/glApi'
import { showErrorNotification, showInfoNotification, showNotification } from '../../shared/helpers'
import { createGlStore } from '../../group_leader/store/createGlStore'
import { extractTournaments, formatEventDate } from '../helpers/results'
import { withoutUserScored } from '../helpers/userScored'
import InstructionSteps from './InstructionSteps'
import LiveBar from './LiveBar'
import ResultsHeader from './ResultsHeader'
import SendScoringLinksModal from './SendScoringLinksModal'
import TeeSheetCard from './TeeSheetCard'
import TournamentLeaderboard from './TournamentLeaderboard'
import UserScoredCard from './UserScoredCard'

const t = makeT('group_leader.event_results')

const STATUS_LIVE = 'in progress'
const STATUS_COMPLETE = 'completed'

// The round's status rarely changes, and the sheet only if the leader edits it in another tab.
const ROUND_POLL_MS = 60000
const SHEET_POLL_MS = 60000

// The heaviest read, so it backs off like the legacy leaderboard; any change resets it.
const WIDGET_POLL_OPTIMISED_MS = 15000
const WIDGET_POLL_UNOPTIMISED_MS = 10000
const WIDGET_POLL_FALLBACK_MULTIPLIER = 2
const WIDGET_POLL_FALLBACK_MAX_MS = 60000

const EventResultsApp = () => {
  const {
    leagueId, roundId: roundIdFromProps, csrfToken, eventName, leagueLbUrlTemplate, eventSetupUrl, roundStatusUrl,
    scoreEntryUrlTemplate, pollingOptimizationDisabled: pollingDisabled, pollingMultiplier, pollingMaxMs,
    scoringLinksSentAt, dateFormat, hourFormat,
  } = useSelector((state) => state.misc.data)

  // In state, as each interval derives from data its own query returns.
  const widgetPollFloorMs = pollingDisabled ? WIDGET_POLL_UNOPTIMISED_MS : WIDGET_POLL_OPTIMISED_MS
  const widgetPollCeilingMs = pollingMaxMs || WIDGET_POLL_FALLBACK_MAX_MS
  const widgetPollMultiplier = pollingMultiplier || WIDGET_POLL_FALLBACK_MULTIPLIER

  const [ roundsPollMs, setRoundsPollMs ] = useState(ROUND_POLL_MS)
  const [ userScoredPollMs, setUserScoredPollMs ] = useState(0)
  const [ widgetPollMs, setWidgetPollMs ] = useState(widgetPollFloorMs)
  const [ finalizing, setFinalizing ] = useState(false)
  const [ lastUpdatedAt, setLastUpdatedAt ] = useState(null)
  const [ sendLinksOpen, setSendLinksOpen ] = useState(false)
  // Once per visit: a leader who closes the dialog is not asked again by the next poll.
  const autoOpenedRef = useRef(false)
  const [ sendingLinks, setSendingLinks ] = useState(false)

  const { data: roundsData, isLoading: roundsLoading, isError: roundsError } =
    useListRoundsQuery(leagueId, { skip: !leagueId, pollingInterval: roundsPollMs })

  // From the list, not the prop: a stale id fell back to data[0] and described another round.
  const round = roundsData?.data?.find((entry) => String(entry.id) === String(roundIdFromProps))
    || (roundIdFromProps ? undefined : roundsData?.data?.[0])
  const roundId = round?.id
  const roundStatus = round?.attributes?.status || 'not started'
  const isLive = roundStatus === STATUS_LIVE
  const isComplete = roundStatus === STATUS_COMPLETE

  const {
    data: widgetData,
    isLoading: widgetLoading,
    isFetching: widgetFetching,
    isError: widgetError,
    refetch: refetchWidget,
  } = useGetTournamentResultsWidgetQuery(
    { leagueId, roundId },
    {
      skip: !leagueId || !roundId,
      pollingInterval: isLive ? widgetPollMs : 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    },
  )

  const { data: teeSheetData, isLoading: sheetLoading } =
    useGetTeeSheetQuery(
      { leagueId, roundId },
      { skip: !leagueId || !roundId, pollingInterval: isLive ? SHEET_POLL_MS : 0 },
    )

  // The scores-in count tells the leaderboards whether to wait for the first score.
  const { data: roundInfo } =
    useGetRoundInformationQuery(
      { leagueId, roundId },
      { skip: !leagueId || !roundId, pollingInterval: isLive ? SHEET_POLL_MS : 0 },
    )

  // Games scored by hand; the widget carries display strings, not the result ids the writes need.
  const { data: userScoredData, refetch: refetchUserScored } = useListUserScoredResultsQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId, pollingInterval: userScoredPollMs },
  )
  const userScoredEvents = userScoredData?.events || []

  const [ adjustUserScoredResult ] = useAdjustUserScoredResultMutation()
  const [ clearUserScoredResult ] = useClearUserScoredResultMutation()
  const [ rerankUserScoredEvent ] = useRerankUserScoredEventMutation()
  // Which event is reordering: the one mutation hook's isLoading would spin every card at once.
  const [ rerankingEventId, setRerankingEventId ] = useState(null)

  const handleUserScoredSave = async (result, field, value) => {
    const response = await adjustUserScoredResult({
      leagueId, roundId, resultId: result.result_id, updateType: field, newValue: value,
    })
    return Boolean(response.error)
  }

  // Recalculates positions from purse and points, then rereads the widget for the leaderboard.
  const handleUserScoredDone = async (event) => {
    setRerankingEventId(event.event_id)
    const response = await rerankUserScoredEvent({ eventId: event.event_id })
    setRerankingEventId(null)
    if (response.error) {
      showErrorNotification(t('user_scored.errors.rerank_failed'))
      refetchUserScored()
    }
    refetchWidget()
  }

  const handleUserScoredClear = async (result) => {
    const response = await clearUserScoredResult({
      leagueId, roundId, aggregateId: result.aggregate_id,
    })
    if (response.error) { return true }
    refetchWidget()
    return false
  }

  // A finalized round's list cannot change, so stop asking.
  useEffect(() => {
    setRoundsPollMs(isComplete ? 0 : ROUND_POLL_MS)
  }, [ isComplete ])

  // The card and the stats count only rows with somebody in them, so blank late-arrival rows do not count.
  const occupiedGroups = (teeSheetData?.data || []).filter((group) => (group.attributes?.players || []).length > 0)
  // Offers the send dialog once on a never-sent, unfinalized round with somebody to send to; above the early returns.
  useEffect(() => {
    if (autoOpenedRef.current || scoringLinksSentAt || isComplete || occupiedGroups.length === 0) { return }
    autoOpenedRef.current = true
    setSendLinksOpen(true)
  }, [ scoringLinksSentAt, isComplete, occupiedGroups.length ])

  // A user-scored game has no rows until the scoring job Finish Setup queues has run, so ask until it has.
  const awaitingUserScored = isLive && occupiedGroups.length > 0
    && userScoredEvents.some((event) => (event.results || []).length === 0)
  useEffect(() => {
    setUserScoredPollMs(awaitingUserScored ? widgetPollFloorMs : 0)
  }, [ awaitingUserScored, widgetPollFloorMs ])

  const [ toggleTournamentStatus ] = useToggleTournamentStatusMutation()
  const [ sendScoringLinks ] = useSendScoringLinksMutation()

  // One effect owns the stamp and the backoff; an unchanged poll keeps the same `data` reference.
  const wasFetching = useRef(false)
  const lastSeenWidget = useRef(undefined)
  useEffect(() => {
    if (widgetFetching) {
      wasFetching.current = true
      return
    }
    if (!wasFetching.current) { return }
    wasFetching.current = false

    if (widgetData !== lastSeenWidget.current) {
      lastSeenWidget.current = widgetData
      if (widgetData) { setLastUpdatedAt(new Date()) }
      setWidgetPollMs(widgetPollFloorMs)
      return
    }
    setWidgetPollMs((current) => Math.min(widgetPollCeilingMs, Math.round(current * widgetPollMultiplier)))
  }, [ widgetFetching, widgetData, widgetPollFloorMs, widgetPollCeilingMs, widgetPollMultiplier ])

  // The templates take the round id once the list has resolved it.
  const withRound = (template) => (template ? template.replace('ROUND_PLACEHOLDER', roundId) : null)

  const handleFinalize = async () => {
    if (!roundStatusUrl || !roundId) {
      showErrorNotification(t('errors.finalize_failed'))
      return
    }

    setFinalizing(true)
    try {
      const result = await toggleTournamentStatus({
        statusUrl: withRound(roundStatusUrl),
        leagueId,
        status: STATUS_COMPLETE,
        csrfToken,
      })
      if (result.error) {
        showErrorNotification(t('errors.finalize_failed'))
      } else {
        showNotification(t('finalized'))
      }
    } finally {
      setFinalizing(false)
    }
  }

  // The monolith resolves the recipients and sends; this triggers it and reports the counts.
  const handleSendScoringLinks = async () => {
    setSendingLinks(true)
    try {
      const result = await sendScoringLinks({ leagueId, roundId })
      if (result.error?.status === 429) {
        // Not a failure: the links just went out and the throttle spares the golfers a second copy.
        showInfoNotification(t('errors.send_scoring_links_rate_limited'))
        setSendLinksOpen(false)
      } else if (result.error) {
        showErrorNotification(t('errors.send_scoring_links_failed'))
      } else {
        const sent = result.data?.sent || {}
        showNotification(t('scoring_links_sent', { emails: sent.emails || 0, texts: sent.texts || 0 }))
        setSendLinksOpen(false)
      }
    } finally {
      setSendingLinks(false)
    }
  }

  if (roundsLoading) {
    return <LoadingState label={t('loading')} className="gl-event-results-load-msg"/>
  }

  // A v3 failure, a nil TMS token, or a page opened without a league.
  if (roundsError || !leagueId) {
    return <ErrorState className="gl-event-results-load-msg"/>
  }

  // No round yet, or a props round id not in the list: the wizard is the way out.
  if (!roundId) {
    return (
      <div className="gl-event-results-card">
        <EmptyState title={t('no_round.title')} subtitle={t('no_round.subtitle')}/>
        {eventSetupUrl && (
          <Button variant="primary" size="md" href={eventSetupUrl}>{t('actions.edit_event')}</Button>
        )}
      </div>
    )
  }

  const tournaments = withoutUserScored(extractTournaments(widgetData), userScoredEvents)
  // Walk-on is group size 1 server-side, as in the wizard: one single-seat foursome per golfer.
  const isWalkon = String(round?.attributes?.pairing_group_size) === '1'
  // Golfers with a hole entered, counted per golfer so team games count right too.
  const scoresIn = roundInfo?.scores_in_number || 0

  const scoreEntryUrl = withRound(scoreEntryUrlTemplate)

  return (
    <div className="gl-event-results-wrap">
      <ResultsHeader
        title={eventName || round?.attributes?.name || t('default_event_name')}
        date={formatEventDate(round?.attributes?.date || round?.attributes?.start_date, dateFormat)}
        isLive={isLive}
        isComplete={isComplete}
        scoreEntryUrl={scoreEntryUrl}
        eventSetupUrl={eventSetupUrl}
        leagueLbUrl={withRound(leagueLbUrlTemplate)}
        finalizing={finalizing}
        onFinalize={handleFinalize}
        onSendScoringLinks={occupiedGroups.length > 0 ? () => setSendLinksOpen(true) : undefined}
        sendingLinks={sendingLinks}
        />

      {sendLinksOpen && (
        <SendScoringLinksModal
          onClose={() => setSendLinksOpen(false)}
          onConfirm={handleSendScoringLinks}
          sending={sendingLinks}
          />
      )}

      {/* Includes the Finalize step, so it shows until the round is finalized. */}
      {!isComplete && (
        <InstructionSteps
          hasScoreEntry={!!scoreEntryUrl}
          hasScoringLinks={occupiedGroups.length > 0}
          />
      )}

      {isLive && (
        <LiveBar
          isFetching={widgetFetching}
          lastUpdatedAt={lastUpdatedAt}
          hourFormat={hourFormat}
          onRefresh={refetchWidget}
          />
      )}

      {!sheetLoading && (
        <TeeSheetCard
          groups={occupiedGroups}
          groupSize={round?.attributes?.pairing_group_size}
          hourFormat={hourFormat}
          isWalkon={isWalkon}
          />
      )}

      {/* Say so and offer the retry: the poll only runs while live. */}
      {widgetError && (
        <div className="gl-event-results-card gl-event-results-load-error">
          <ErrorState message={t('errors.results_failed')}/>
          <Button variant="secondary" size="md" onClick={refetchWidget}>{t('actions.retry')}</Button>
        </div>
      )}

      {!widgetError && !widgetLoading && tournaments.length === 0 && userScoredEvents.length === 0 && (
        <div className="gl-event-results-card">
          <EmptyState title={t('no_games')}/>
        </div>
      )}

      {userScoredEvents.map((event) => (
        <UserScoredCard
          key={event.event_id}
          event={event}
          onSave={handleUserScoredSave}
          onClear={handleUserScoredClear}
          onDone={() => handleUserScoredDone(event)}
          reranking={rerankingEventId === event.event_id}
          />
      ))}

      <div className="gl-event-results-games-list">
        {tournaments.map((tournament, index) => (
          <TournamentLeaderboard
            key={tournament.id || tournament.tournament_id || index}
            tournament={tournament}
            isLive={isLive}
            hasScores={scoresIn > 0}
            />
        ))}
      </div>
    </div>
  )
}

// Every prop is boot data the steps and v3BaseQuery read back out of state.misc.
const GroupLeaderEventResultsRoot = (props) => {
  const storeRef = useRef(null)

  if (!storeRef.current) {
    storeRef.current = createGlStore()
    // refetchOnFocus needs the browser listeners RTK Query does not attach itself
    setupListeners(storeRef.current.dispatch)
    storeRef.current.dispatch(setMiscData(props))
  }

  return (
    <Provider store={storeRef.current}>
      <EventResultsApp/>
      {/* Every notification on this page renders here. */}
      <NotificationContainer/>
    </Provider>
  )
}

GroupLeaderEventResultsRoot.propTypes = {
  customerId: PropTypes.string,
  groupId: PropTypes.string,
  leagueId: PropTypes.string,
  roundId: PropTypes.string,
  tmsApiBaseUrl: PropTypes.string,
  tmsApiToken: PropTypes.string,
  csrfToken: PropTypes.string,
  // The league name — what the group leader called the event
  eventName: PropTypes.string,
  // Full leaderboards page, with ROUND_PLACEHOLDER for the id (substituted once known)
  leagueLbUrlTemplate: PropTypes.string,
  eventSetupUrl: PropTypes.string,
  // Round-status endpoint, with ROUND_PLACEHOLDER for the id
  roundStatusUrl: PropTypes.string,
  // Score entry, likewise placeholder-substituted once the round id is known
  scoreEntryUrlTemplate: PropTypes.string,
  // The customer's admin date order as a token pattern ("mm-dd-yyyy", "dd-mm-yyyy", "yyyy-mm-dd")
  dateFormat: PropTypes.string,
  // The customer's admin hour setting: 'am_pm' or '24h', which the live bar's clock follows
  hourFormat: PropTypes.string,
  // The legacy leaderboard's polling settings; absent means this page's own fallbacks.
  pollingOptimizationDisabled: PropTypes.bool,
  pollingMultiplier: PropTypes.number,
  pollingMaxMs: PropTypes.number,
  // When this round last had its scoring links sent; blank means never.
  scoringLinksSentAt: PropTypes.string,
}

export default GroupLeaderEventResultsRoot
